/**
 * The entity route modules' `clientLoader` (web plan W4.4): an in-app
 * navigation to a brand, shop, store or promotion page never asks the SEO
 * server (its decisions are made per document, server/framework.mjs). It
 * warms the browser's Apollo cache with exactly the queries the detail page
 * runs (@/graphql/entityRoutes: the slug's `resolveEntityPath`, then the
 * `*ById` query), so the page renders its data as soon as the navigation
 * lands; the page keeps its own states (not found, failed load, the move to
 * the canonical path) and renders its own head (`<PageHead/>`, web plan
 * W5.1). A failed or slow prefetch only means the page shows its skeleton:
 * after `timeoutMs` the navigation goes ahead and the page's queries pick up
 * the request already in flight.
 */
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { ShouldRevalidateFunctionArgs } from 'react-router';
import {
  ENTITY_DETAIL_QUERIES,
  detailVariables,
  parseEntityRoute,
  resolveVariables,
} from '@/graphql/entityRoutes';
import { RESOLVE_ENTITY_PATH } from '@/graphql/queries';
import type { EntityPathResolution } from '@/hooks/useAppData';
import { getApolloClient } from '@/lib/apollo';
import { EMPTY_ENTITY_ROUTE_DATA, type EntityRouteData } from '@/seo/entityDocument';
import type { EntityType } from '@/seo/urls';
import { isObjectId } from '@/utils/detailStatus';

/** How long a navigation waits for the prefetch before it shows the page's skeleton. */
export const ENTITY_PREFETCH_TIMEOUT_MS = 4000;

/** A 200 or a redirect naming an entity: its id (as `useDetailQuery` reads it). */
function resolvedId(answer: EntityPathResolution | null | undefined): string | null {
  if (!answer) return null;
  const named = answer.status === 200 || (answer.status >= 300 && answer.status < 400);
  return named && isObjectId(answer.id) ? answer.id : null;
}

/**
 * Runs the detail page's queries for `pathname` into `client`'s cache; never
 * rejects. `type` is the route module's: a path naming another type is left
 * to the page.
 */
export async function prefetchEntityPage(
  type: EntityType,
  pathname: string,
  {
    client = getApolloClient(),
    timeoutMs = ENTITY_PREFETCH_TIMEOUT_MS,
  }: { client?: ApolloClient<NormalizedCacheObject>; timeoutMs?: number } = {},
): Promise<void> {
  const route = parseEntityRoute(pathname);
  if (!route || route.type !== type || !route.ref) return;
  const work = (async () => {
    let id: string | null = isObjectId(route.ref) ? route.ref : null;
    if (!id) {
      const { data } = await client.query<{ resolveEntityPath: EntityPathResolution | null }>({
        query: RESOLVE_ENTITY_PATH,
        variables: resolveVariables(type, route.ref, route.locale),
      });
      id = resolvedId(data?.resolveEntityPath);
    }
    if (id) {
      await client.query({ query: ENTITY_DETAIL_QUERIES[type].query, variables: detailVariables(id, route.locale) });
    }
  })().catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  try {
    await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** `clientLoader` for a `type` route: prefetch, then no loader data (the page reads Apollo). */
export function entityClientLoader(type: EntityType) {
  return async ({ request }: { request: Request }): Promise<EntityRouteData> => {
    await prefetchEntityPage(type, new URL(request.url).pathname);
    return EMPTY_ENTITY_ROUTE_DATA;
  };
}

/**
 * `shouldRevalidate` for the entity routes: a change of the query string alone
 * (tabs, filters) on the same pathname keeps the loader data. Revalidating
 * would re-run the server loader, whose answer is per document, and reset
 * `loaded.entity` to null.
 */
export function entityShouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: Pick<ShouldRevalidateFunctionArgs, 'currentUrl' | 'nextUrl' | 'defaultShouldRevalidate'>): boolean {
  if (currentUrl.pathname === nextUrl.pathname) return false;
  return defaultShouldRevalidate;
}
